import { AcButton } from '../ui';
import { metricValue, type ClinicCash, type CashMetric } from './report-types';
/** Render the Money reader's admitted facts; never calculate a replacement cash chain. */
export default function ReportCash({
  cash,
  reason,
  onOpen,
}: {
  cash: ClinicCash | null;
  reason: string;
  onOpen: (metric: CashMetric) => void;
}) {
  const complete = cash && !cash.summary.collectionsCapped && !cash.summary.entriesCapped;
  const values: [string, number | null][] = [
    ['Opening balance', complete ? cash.summary.openingMinorUnits : null],
    ['Cash receipts', complete ? cash.days.reduce((n, d) => n + d.cashInMinorUnits, 0) : null],
    ['Bank deposits', complete ? cash.summary.depositsMinorUnits : null],
    ['Cash expenses', complete ? cash.summary.expensesMinorUnits : null],
    [
      'Expected closing',
      complete ? (cash.days[cash.days.length - 1]?.derivedCloseMinorUnits ?? null) : null,
    ],
  ];
  return (
    <section className="clinic-report__cash">
      <h2>Clinic cash</h2>
      <div className="clinic-report__cash-band">
        {values.map(([label, value], index) => (
          <div key={label}>
            <span>{label}</span>
            <AcButton
              variant="ghost"
              className="clinic-report__metric"
              aria-label={`View ${label.toLowerCase()} evidence`}
              disabled={!cash}
              onClick={() =>
                onOpen(
                  (
                    [
                      'cashOpening',
                      'cashReceipts',
                      'cashDeposits',
                      'cashExpenses',
                      'cashClosing',
                    ] as CashMetric[]
                  )[index]!,
                )
              }
            >
              {metricValue(value, 'minor_units')}
            </AcButton>
          </div>
        ))}
      </div>
      {!cash ? (
        <p>{reason}</p>
      ) : (
        <>
          <p>
            Clinic-wide cash · independent of doctor filter · {cash.chain.state.replace(/_/g, ' ')}
            {!complete ? ' · Complete coverage unavailable; amounts withheld.' : ''}
          </p>
          <p>
            Counted adjustment{' '}
            {metricValue(complete ? cash.summary.countedAdjustmentMinorUnits : null, 'minor_units')}{' '}
            · Expected closing uses Money’s current derived balance. Counted closing remains
            separate in day evidence. Post-close correction{' '}
            {metricValue(cash.postCloseAdjustmentMinorUnits, 'minor_units')} ·{' '}
            {cash.postCloseAdjustmentStatus?.replace(/_/g, ' ') ??
              'Post-close status unavailable in this source version.'}
          </p>
        </>
      )}
    </section>
  );
}
